import React, { useMemo, useState } from "react";
import { Box, Button, Typography } from "@material-ui/core";
import { LoadingButton } from "@material-ui/lab";
import { Icon } from "@iconify/react";
import alertIcon from "@iconify-icons/eva/alert-triangle-fill";
import creditCardIcon from "@iconify-icons/eva/credit-card-fill";
import { alpha } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useSelector } from "src/redux/store";
import axios from "src/utils/axios";
import IsRole from "src/utils/IsRole";
import Roles from "src/utils/Roles";
import MyAutoSnack from "src/components/MyAutoSnack";
import { PATH_APP } from "src/routes/paths";
import { useHistory } from "react-router";

// ─── Reglas de escalado ──────────────────────────────────────────────
// Grace period fijo de 7 días desde `PastDueSince` — coherente con
// `PaymentService._gracePeriodDays = 7` en backend y con la plantilla
// de emails dunning (SendEmailDunning día 0-2 / 3-5 / 6+).
//
// Nivel visual:
//   0-2 → warning (amarillo)   "Estamos reintentando el cobro"
//   3-5 → warning fuerte       "Actualiza tu tarjeta — quedan X días"
//   6-7 → error (rojo)         "Último aviso — se suspenderá mañana"
//
// Umbrales alineados con el dunning para que email + banner cuenten la
// misma historia al cliente.
const GRACE_DAYS = 7;

/**
 * Selector para que `DashboardLayout` sepa si tiene que renderizar el
 * wrapper de banners (evita un nodo fixed inútil si no hay alertas).
 * `IsRole` no se puede llamar aquí (selector puro) — filtrado por rol
 * lo hace el componente al render. Es OK montar el nodo aunque el
 * componente devuelva null en algunos roles: el wrapper mide 0px.
 */
export function selectHasSubscriptionAlert(state) {
  const status = state?.authJwt?.user?.subscriptionStatus;
  return status === "past_due" || status === "unpaid";
}

function computeLevel(daysSincePastDue) {
  if (daysSincePastDue >= 6) return "critical";
  if (daysSincePastDue >= 3) return "urgent";
  return "gentle";
}

const LEVEL_STYLES = {
  gentle: {
    bg: "#FFF4E5",           // amarillo suave
    border: "#FFB74D",
    text: "#663C00",
    accent: "#FF9800",
    title: "Cobro de tu suscripción fallido",
  },
  urgent: {
    bg: "#FFE0B2",           // naranja
    border: "#FB8C00",
    text: "#6B2E00",
    accent: "#E65100",
    title: "Actualiza tu método de pago",
  },
  critical: {
    bg: "#FFEBEE",           // rojo
    border: "#D32F2F",
    text: "#7F0000",
    accent: "#B71C1C",
    title: "Último aviso — servicio a punto de suspenderse",
  },
};

function daysBetween(fromIso, toDate) {
  if (!fromIso) return 0;
  const from = new Date(fromIso);
  if (Number.isNaN(from.getTime())) return 0;
  const ms = toDate.getTime() - from.getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

/**
 * Banner persistente cuando la suscripción del restaurante está en
 * `past_due`. Se pinta en `DashboardLayout` — la vista TPV camarero
 * (`TpvCartaLayout`) NO lo lleva porque el camarero no puede resolver
 * el problema y no aporta al servicio.
 *
 * Solo se muestra al titular de la cuenta (USER_NORMAL / USER_ADMIN /
 * USER_DISTRIBUTOR). El empleado (USER_EMPLOYEE) no lo ve — no tiene
 * acceso al Customer Portal ni a Billing y solo generaría estrés
 * durante el servicio.
 */
export default function SubscriptionAlertBanner() {
  const { user } = useSelector((state) => state.authJwt);
  const { claims } = useSelector((state) => state.authJwt);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  // El campo llega en el DTO como `subscriptionStatus`. "past_due" y
  // "unpaid" ambos merecen la alerta — "unpaid" es el estado terminal
  // que Stripe fija tras agotar reintentos si `pause_collection` no
  // está configurado; visualmente igual de crítico.
  const status = user?.subscriptionStatus;
  const isInGrace = status === "past_due" || status === "unpaid";

  // Solo el titular ve el banner.
  const isOwner =
    IsRole(claims?.role, Roles.USER_NORMAL) ||
    IsRole(claims?.role, Roles.USER_ADMIN) ||
    IsRole(claims?.role, Roles.USER_DISTRIBUTOR);

  const level = useMemo(() => {
    if (!isInGrace) return null;
    const days = daysBetween(user?.pastDueSince, new Date());
    return { days, name: computeLevel(days) };
  }, [isInGrace, user?.pastDueSince]);

  if (!isInGrace || !isOwner || !level) return null;

  const styles = LEVEL_STYLES[level.name];
  const daysLeft = Math.max(0, GRACE_DAYS - level.days);

  const message = (() => {
    if (level.name === "critical") {
      return daysLeft <= 1
        ? "Tu suscripción se suspenderá en menos de 24 h si no actualizas tu método de pago."
        : `Tu suscripción se suspenderá en ${daysLeft} días. Actualiza tu tarjeta ahora para evitar la interrupción del servicio.`;
    }
    if (level.name === "urgent") {
      return `Llevamos ${level.days} días intentando cobrar tu suscripción sin éxito. Quedan ${daysLeft} días antes de que se suspenda el servicio.`;
    }
    return "Estamos reintentando el cobro de tu suscripción. Si tu tarjeta ha caducado o cambió, actualízala para evitar interrupciones.";
  })();

  const handleOpenPortal = async () => {
    setLoading(true);
    try {
      const userId = window.localStorage.getItem("userId");
      const res = await axios.post(`/payment/portal/${userId}`);
      if (res?.data?.url) {
        window.open(res.data.url, "_blank", "noopener,noreferrer");
      } else {
        MyAutoSnack(
          "error",
          "No se pudo abrir el portal de facturación",
          enqueueSnackbar,
          closeSnackbar,
        );
      }
    } catch (_) {
      MyAutoSnack(
        "error",
        "No se pudo abrir el portal de facturación",
        enqueueSnackbar,
        closeSnackbar,
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        bgcolor: styles.bg,
        borderBottom: `3px solid ${styles.border}`,
        borderTop: `1px solid ${alpha(styles.border, 0.5)}`,
        px: { xs: 1.5, sm: 2.5 },
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: { xs: 1, sm: 2 },
        flexWrap: "wrap",
        // El banner NO se puede cerrar — es intencional. El titular tiene
        // que ver esto en cada carga hasta que actualice el pago o la
        // suscripción se cancele definitivamente y el estado deje de ser
        // past_due (webhook `customer.subscription.updated`).
      }}
    >
      <Box sx={{ color: styles.accent, display: "flex", flexShrink: 0 }}>
        <Icon icon={alertIcon} width={22} height={22} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 240 }}>
        <Typography
          variant="subtitle2"
          sx={{
            color: styles.text,
            fontWeight: 800,
            textTransform: "uppercase",
            letterSpacing: 0.5,
            lineHeight: 1.2,
          }}
        >
          {styles.title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: styles.text, mt: 0.25, lineHeight: 1.35 }}
        >
          {message}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
        <LoadingButton
          variant="contained"
          size="small"
          startIcon={<Icon icon={creditCardIcon} width={18} height={18} />}
          onClick={handleOpenPortal}
          loading={loading}
          sx={{
            bgcolor: styles.accent,
            color: "#fff",
            fontWeight: 700,
            "&:hover": { bgcolor: styles.border },
          }}
        >
          Actualizar método de pago
        </LoadingButton>
        <Button
          variant="outlined"
          size="small"
          onClick={() => history.push(PATH_APP.user.account)}
          sx={{
            color: styles.text,
            borderColor: alpha(styles.accent, 0.5),
            "&:hover": { borderColor: styles.accent, bgcolor: alpha(styles.accent, 0.08) },
          }}
        >
          Ver facturación
        </Button>
      </Box>
    </Box>
  );
}
